import { EditableInfoContainerGrid } from '@dealstack/frontend/shared/components/EditableInfoContainer/styled.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Button, Divider, Flex, Text } from '@mantine/core';
import { PropsWithChildren } from 'react';
import type { DetailEditableInfoRowProps } from './EditableInfoContainer.types';
import { EDITABLE_INFO_LABEL_COLUMN_WIDTH } from './styled';

export const EditableInfoContainer = ({
	title,
	disableButtons,
	onAdd,
	onRemove,
	onUpdate,
	onCancel,
	isEdit,
	setIsEdit,
	hideRemove,
	hasEditorPermission = true,
	updateBtnDataTestId,
	editBtnDataTestId,
	skipTopBorder,
	children,
	childrenPadding,
	useGrid,
	gridColumns = 2,
}: PropsWithChildren<DetailEditableInfoRowProps>) => {
	const tCommonUi = useTranslationCommonUi();
	const titleElement = (
		<Text
			fw={600}
			lineClamp={1}
			w="100%"
			truncate
			style={{ textOverflow: 'ellipsis', overflow: 'hidden', wordBreak: 'break-all' }}
		>
			{title}
		</Text>
	);

	const closeEdit = () => {
		onCancel?.();
		setIsEdit?.(false);
	};

	return (
		<>
			{skipTopBorder ? null : <Divider c="gray.4" />}
			<Flex bg={isEdit ? 'gray.0' : 'white'} w="100%">
				<Flex align="flex-start" w="100%">
					<Flex
						direction="column"
						gap="0.5rem"
						p="0.5rem"
						miw={EDITABLE_INFO_LABEL_COLUMN_WIDTH}
						maw={EDITABLE_INFO_LABEL_COLUMN_WIDTH}
						h="100%"
						sx={(theme) => ({
							borderRight: `1px solid ${isEdit ? theme.colors.gray[4] : 'transparent'}`,
						})}
					>
						{isEdit ? (
							<>
								<Flex align="center" justify="space-between" gap="0.5rem">
									{titleElement}
									{hideRemove ? null : (
										<Button
											variant="subtle"
											color="red"
											size="xs"
											disabled={disableButtons}
											onClick={onRemove}
											style={{ flexShrink: 0 }}
										>
											{tCommonUi('buttons.remove')}
										</Button>
									)}
								</Flex>
								<Flex align="center" gap="0.5rem">
									<Button
										variant="light"
										color="gray"
										bg="gray.4"
										c="dark.8"
										fw="400"
										onClick={closeEdit}
										disabled={disableButtons}
									>
										{tCommonUi('buttons.cancel')}
									</Button>
									<Button
										variant="outline"
										bg="white.0"
										fw="400"
										onClick={onUpdate}
										disabled={disableButtons || !hasEditorPermission}
										data-testid={updateBtnDataTestId}
									>
										{tCommonUi('buttons.update')}
									</Button>
								</Flex>
							</>
						) : (
							<Flex direction="column" align="start" gap="0.5rem">
								{titleElement}
								<Flex align="center" wrap="wrap" gap="0.5rem">
									{!!setIsEdit && (
										<Button
											variant="subtle"
											fw="600"
											color="blue.6"
											leftSection={<Icon variant="pencil" size="lg" />}
											onClick={() => setIsEdit(true)}
											disabled={disableButtons || !hasEditorPermission}
											data-testid={editBtnDataTestId}
										>
											{tCommonUi('button.edit')}
										</Button>
									)}
									{!!onAdd && (
										<Button
											variant="subtle"
											fw="600"
											color="blue.6"
											leftSection={<Icon variant="plus" size="lg" />}
											onClick={onAdd}
											disabled={!hasEditorPermission}
										>
											{tCommonUi('button.add')}
										</Button>
									)}
								</Flex>
							</Flex>
						)}
					</Flex>

					{useGrid ? (
						<EditableInfoContainerGrid
							$gridColumns={gridColumns}
							$padding={childrenPadding}
						>
							{children}
						</EditableInfoContainerGrid>
					) : (
						<Flex p={childrenPadding} w="100%" style={{ position: 'relative' }}>
							{children}
						</Flex>
					)}
				</Flex>
			</Flex>
		</>
	);
};
